import { createStore } from 'react-sweet-state';
import { cloneDeep, isEqual, orderBy } from 'lodash';

import {
  queryChannelsAuthState,
  extractPerformanceCenterTextVariations,
  getPerformanceCenterResultDocument,
  performanceCenterUpdateTag,
  queryProjectForNewCopies,
  performanceCenterUpdateConnectedAccounts,
  connectedAccounts,
  queryPerformanceCenterTextVariations,
  queryPerformanceCenterAdditionalData,
  createReviewsAccount,
  reviewsAccounts,
  websiteAccounts,
  createWebsiteAccount,
} from 'src/graphql/performance';
import { alignChannelWithBackend, CHANNEL } from 'src/data/channel';
import { isAsyncRequestError } from 'src/lib/errors';
import {
  CIP_TAG_STATUS,
  CIP_TAG_ACTION_TYPE,
  TALKING_POINT_FIELD,
  ADS_CHANNELS,
  CIP_ADS_PER_PAGE,
  REVIEWS_CHANNELS,
  WEBSITE_CHANNELS,
} from 'src/data/performance';
import { SORT_DIRECTION } from 'src/data/common';
import { SOCIAL_AUTH_STATE } from 'src/data/user';
import {
  connectToChannel,
  isCopiesExtractionError,
  queryChannelAccounts,
  waitForCopiesExtraction,
} from './utils';

const NAME = 'performance-center-store';

const mapAd = (ad) => ({
  ...ad,
  weightedCtr: ad.weightedAvgCtr,
  impressions: ad.lifetimeImpressions,
});
const prepareCopies = (copies) => copies.map(mapAd);
const prepareAccountList = (accounts, selectedAccountIds = []) =>
  accounts
    .map((account) => ({
      ...account,
      isConnected: selectedAccountIds.includes(account.id),
    }))
    .sort((a, b) => b.isConnected - a.isConnected);

const INITIAL_STATE = {
  loadingAuthState: false,
  connectingToChannel: {},

  loadingCopies: {},
  extractingCopies: {},

  authState: {},
  authStateFetched: false,

  accounts: {},

  selectedAccountIds: {},

  copies: {},

  lastExtractionDate: {},

  copiesSortField: null,
  copiesSortDirection: null,

  accountsSortField: null,
  accountsSortDirection: null,

  tagsForSuggestion: [],
  tagsForFilter: {},
  updatingTag: false,

  campaigns: {},

  clearCounter: 0,

  talkingPoints: {},

  avgCompletionPercent: {},

  campaignsCopyImpressions: {},

  loadingConnectedAccounts: true,
  connectedAccountsFetched: false,

  workspacesDataCache: {},

  authErrorMessage: {},
};

const channelAbortControllers = new Map();

/**
 * Private actions
 */
export const PRIVATE_ACTIONS = {
  clearStore:
    (workspaceId) =>
    ({ getState, setState, dispatch }) => {
      dispatch(PRIVATE_ACTIONS.storeWorkspaceDataInCache(workspaceId));
      const { clearCounter, workspacesDataCache } = getState();

      setState({
        ...cloneDeep(INITIAL_STATE),
        workspacesDataCache,
        clearCounter,
      });
      dispatch(PRIVATE_ACTIONS.increaseClearCounter());
    },

  increaseClearCounter:
    () =>
    ({ getState, setState }) => {
      const { clearCounter } = getState();
      setState({ clearCounter: clearCounter + 1 });
    },

  storeWorkspaceDataInCache:
    (workspaceId) =>
    ({ getState, setState }) => {
      const {
        copies,
        accounts,
        selectedAccountIds,
        authState,
        authStateFetched,
        talkingPoints,
        lastExtractionDate,
        tagsForSuggestion,
        tagsForFilter,
        campaigns,
        campaignsCopyImpressions,
        connectedAccountsFetched,
        loadingConnectedAccounts,
        workspacesDataCache,
        connectingToChannel,
      } = getState();

      setState({
        workspacesDataCache: {
          ...workspacesDataCache,
          [workspaceId]: {
            copies,
            accounts,
            selectedAccountIds,
            authState,
            authStateFetched,
            talkingPoints,
            lastExtractionDate,
            tagsForSuggestion,
            tagsForFilter,
            campaigns,
            campaignsCopyImpressions,
            connectedAccountsFetched,
            loadingConnectedAccounts,
            connectingToChannel,
          },
        },
      });
    },

  initAccountList:
    (accounts, channel) =>
    ({ dispatch }) => {
      const sorted = orderBy(accounts, 'name', SORT_DIRECTION.asc);
      const channelSelectedAccountIds = dispatch(
        PRIVATE_ACTIONS.getChannelSelectedAccountIds(channel)
      );

      const preparedAccountList = prepareAccountList(sorted, channelSelectedAccountIds);

      dispatch(PRIVATE_ACTIONS.setChannelAccounts(channel, preparedAccountList));
    },

  initCopyList:
    (copies, channel) =>
    ({ dispatch }) => {
      if (!copies?.length) {
        return;
      }
      const preparedCopies = prepareCopies(copies || []);

      const sorted = orderBy(preparedCopies, 'sortIndex', SORT_DIRECTION.asc);

      dispatch(PRIVATE_ACTIONS.setChannelCopies(channel, sorted));
    },

  initTalkingPointList:
    (talkingPoints, channel) =>
    ({ dispatch }) => {
      const sorted = orderBy(talkingPoints, TALKING_POINT_FIELD.rating, SORT_DIRECTION.desc);

      dispatch(PRIVATE_ACTIONS.setChannelTalkingPoints(channel, sorted));
    },

  initSelectedAccountIds:
    (accountIds, channel) =>
    ({ getState, setState }) => {
      const { selectedAccountIds } = getState();
      setState({ selectedAccountIds: { ...selectedAccountIds, [channel]: accountIds || [] } });
    },

  setAvgCompletionPercent:
    (newAvgCompletionPercent, channel) =>
    ({ getState, setState }) => {
      const { avgCompletionPercent } = getState();

      setState({
        avgCompletionPercent: { ...avgCompletionPercent, [channel]: newAvgCompletionPercent },
      });
    },

  clearSelectedAccountIds:
    (channel) =>
    ({ dispatch }) => {
      const accounts = dispatch(PRIVATE_ACTIONS.getChannelAccounts(channel));

      dispatch(
        ACTIONS.updateConnectedAccounts({
          selectedAccountIds: [],
          channel,
        })
      );

      const preparedAccountList = prepareAccountList(accounts, []);

      dispatch(PRIVATE_ACTIONS.setChannelAccounts(channel, preparedAccountList));
    },

  initLastExtractionDate:
    (newLastExtractionDate, channel) =>
    ({ getState, setState }) => {
      const { lastExtractionDate } = getState();

      setState({ lastExtractionDate: { ...lastExtractionDate, [channel]: newLastExtractionDate } });
    },

  getChannelLastExtractionDate:
    (channel) =>
    ({ getState }) => {
      const { lastExtractionDate } = getState();

      return lastExtractionDate[channel];
    },

  initTags:
    (tagsForSuggestion, newTagsForFilter, channel) =>
    ({ getState, setState }) => {
      const { tagsForFilter } = getState();

      setState({
        tagsForSuggestion: tagsForSuggestion || [],
      });

      setState({
        tagsForFilter: { ...tagsForFilter, [channel]: newTagsForFilter || [] },
      });
    },

  initCampaigns:
    (newCampaigns, channel) =>
    ({ getState, setState }) => {
      const { campaigns } = getState();

      setState({
        campaigns: { ...campaigns, [channel]: newCampaigns || [] },
      });
    },

  getChannelCopies:
    (channel) =>
    ({ getState }) => {
      const { copies } = getState();

      return copies[channel] || [];
    },

  setChannelCopies:
    (channel, newCopies) =>
    ({ getState, setState }) => {
      const { copies } = getState();

      setState({ copies: { ...copies, [channel]: newCopies } });
    },

  setChannelTalkingPoints:
    (channel, newTalkingPoints) =>
    ({ getState, setState }) => {
      const { talkingPoints } = getState();

      setState({
        talkingPoints: { ...talkingPoints, [channel]: newTalkingPoints },
      });
    },

  setChannelCampaignsCopyImpressions:
    (newCampaignsCopyImpressions, channel) =>
    ({ getState, setState }) => {
      const { campaignsCopyImpressions } = getState();

      setState({
        campaignsCopyImpressions: {
          ...campaignsCopyImpressions,
          [channel]: newCampaignsCopyImpressions,
        },
      });
    },

  mergeChannelCopies:
    (channel, newCopies) =>
    ({ getState, setState }) => {
      const { copies } = getState();

      setState({ copies: { ...copies, [channel]: newCopies } });
    },

  getChannelAccounts:
    (channel) =>
    ({ getState }) => {
      const { accounts } = getState();

      return accounts[channel];
    },

  setChannelAccounts:
    (channel, newAccounts) =>
    ({ getState, setState }) => {
      const { accounts } = getState();

      setState({
        accounts: { ...accounts, [channel]: newAccounts },
      });
    },

  getChannelSelectedAccountIds:
    (channel) =>
    ({ getState }) => {
      const { selectedAccountIds } = getState();

      return selectedAccountIds[channel] || [];
    },

  setChannelSelectedAccountIds:
    (channel, newSelectedAccountIds) =>
    ({ getState, setState }) => {
      const { selectedAccountIds, copies, lastExtractionDate } = getState();

      // clear copies if new newSelectedAccountIds are not the same
      if (!isEqual(selectedAccountIds[channel], newSelectedAccountIds)) {
        setState({
          copies: { ...copies, [channel]: [] },
          lastExtractionDate: { ...lastExtractionDate, [channel]: null },
        });
      }

      setState({
        selectedAccountIds: { ...selectedAccountIds, [channel]: newSelectedAccountIds },
      });
    },

  addTagForFilter:
    (channel, tag) =>
    ({ getState, setState }) => {
      const { tagsForFilter } = getState();

      if (tagsForFilter[channel].some((item) => item.tagName === tag.tagName)) {
        return;
      }

      const newTagsForFilter = [...tagsForFilter[channel], tag];

      setState({ tagsForFilter: { ...tagsForFilter, [channel]: newTagsForFilter } });
    },

  removeTagForFilter:
    (channel, tagNameToDelete) =>
    ({ getState, setState }) => {
      const { tagsForFilter } = getState();

      if (!tagsForFilter[channel]) {
        return;
      }

      const newTagsForFilter = tagsForFilter[channel].filter(
        (tag) => tag.tagName !== tagNameToDelete
      );

      setState({
        tagsForFilter: { ...tagsForFilter, [channel]: newTagsForFilter },
      });
    },

  addTagForSuggestion:
    (tag) =>
    ({ getState, setState }) => {
      const { tagsForSuggestion } = getState();
      if (tagsForSuggestion.some((item) => item.tagName === tag.tagName)) {
        return;
      }
      setState({ tagsForSuggestion: [...tagsForSuggestion, tag] });
    },

  removeTagForSuggestion:
    (tagNameToDelete) =>
    ({ getState, setState }) => {
      const { tagsForSuggestion } = getState();
      const newList = tagsForSuggestion.filter((tag) => tag.tagName !== tagNameToDelete);
      setState({
        tagsForSuggestion: newList,
      });
    },

  setLoadingCopies:
    (channel, loading) =>
    ({ getState, setState }) => {
      const { loadingCopies } = getState();

      setState({
        loadingCopies: { ...loadingCopies, [channel]: loading },
      });
    },

  setExtractingCopies:
    (channel, extracting) =>
    ({ getState, setState }) => {
      const { extractingCopies } = getState();

      setState({
        extractingCopies: { ...extractingCopies, [channel]: extracting },
      });
    },

  setConnectingToChannel:
    (channel, connecting) =>
    ({ getState, setState }) => {
      const { connectingToChannel } = getState();

      setState({
        connectingToChannel: { ...connectingToChannel, [channel]: connecting },
      });
    },

  getChannelLoadingCopies:
    (channel) =>
    ({ getState }) => {
      const { loadingCopies } = getState();

      return loadingCopies[channel];
    },

  getChannelExtractingCopies:
    (channel) =>
    ({ getState }) => {
      const { extractingCopies } = getState();

      return extractingCopies[channel];
    },

  getOtherChannels: (channel) => () => ADS_CHANNELS.filter((c) => c !== channel),

  queryRestOfTextVariationsData:
    (channel, workspaceId) =>
    async ({ dispatch }) => {
      const alignedChannel = alignChannelWithBackend(channel);

      const result = await queryPerformanceCenterTextVariations(alignedChannel, workspaceId);

      channelAbortControllers.delete(channel);

      dispatch(PRIVATE_ACTIONS.initCopyList(result?.copies, channel));
      dispatch(PRIVATE_ACTIONS.initSelectedAccountIds(result?.accountIds, channel));
      dispatch(PRIVATE_ACTIONS.initTags(result?.tagsForSuggestion, result?.tagsForFilter, channel));
      dispatch(PRIVATE_ACTIONS.initCampaigns(result?.campaigns, channel));
    },
};

/**
 * Public available actions
 */
export const ACTIONS = {
  init:
    () =>
    ({ setState }) => {
      setState(cloneDeep(INITIAL_STATE));
    },
  queryChannelsAuthState:
    (force) =>
    async ({ getState, setState }) => {
      try {
        const { authState, authStateFetched } = getState();
        if (authStateFetched && !force) {
          return;
        }
        setState({ loadingAuthState: true });

        const { googleAuthState, facebookAuthState, linkedinAuthState } =
          await queryChannelsAuthState();

        authState[CHANNEL.google] = googleAuthState;
        authState[CHANNEL.facebook] = facebookAuthState;
        authState[CHANNEL.linkedin] = linkedinAuthState;

        setState({
          authState,
          authStateFetched: true,
        });
      } finally {
        setState({ loadingAuthState: false });
      }
    },

  connectToChannel:
    (channel) =>
    async ({ getState, setState, dispatch }) => {
      dispatch(PRIVATE_ACTIONS.setConnectingToChannel(channel, true));

      const { authState } = getState();

      try {
        const result = await connectToChannel(channel);

        setState({ authState: { ...authState, [channel]: result?.authState } });

        // Init accounts list
        const accounts = result?.adAccounts || [];
        dispatch(PRIVATE_ACTIONS.initAccountList(accounts, channel));
      } finally {
        dispatch(PRIVATE_ACTIONS.setConnectingToChannel(channel, false));
        setState({ loadingAccounts: false });
      }
    },

  isChannelConnected:
    (channel) =>
    ({ getState }) => {
      const { authState } = getState();
      return authState[channel] === SOCIAL_AUTH_STATE.authorized;
    },

  queryAdAccounts:
    (channel) =>
    async ({ setState, dispatch }) => {
      setState({ loadingAccounts: true });
      try {
        const result = await queryChannelAccounts(channel);
        const accounts = result || [];
        dispatch(PRIVATE_ACTIONS.initAccountList(accounts, channel));
      } finally {
        setState({ loadingAccounts: false });
      }
    },

  queryReviewsAccounts:
    () =>
    async ({ dispatch, setState }, { notifications, currentWorkspaceId }) => {
      setState({ loadingAccounts: true });
      try {
        const result = await reviewsAccounts(currentWorkspaceId);
        const accounts = result || [];
        REVIEWS_CHANNELS.forEach((channel) => {
          const filteredAccounts = accounts.filter((account) => account.siteType === channel);
          dispatch(PRIVATE_ACTIONS.initAccountList(filteredAccounts, channel));
        });

        setState({ loadingAccounts: false });
      } catch (error) {
        notifications.displayError(error);
      }
    },

  queryWebsiteAccounts:
    (channel) =>
    async ({ dispatch, setState }, { notifications, currentWorkspaceId }) => {
      setState({ loadingAccounts: true });
      try {
        const result = await websiteAccounts(currentWorkspaceId);
        const accounts = result || [];
        WEBSITE_CHANNELS.forEach((channel) => {
          const filteredAccounts = accounts.filter((account) => account.websiteType === channel);
          dispatch(PRIVATE_ACTIONS.initAccountList(filteredAccounts, channel));
        });

        setState({ loadingAccounts: false });
      } catch (error) {
        notifications.displayError(error);
      }
    },

  queryConnectedAccounts:
    (workspaceId) =>
    async ({ getState, dispatch, setState }, { notifications }) => {
      const { connectedAccountsFetched } = getState();
      if (connectedAccountsFetched) {
        return;
      }

      try {
        const results = await connectedAccounts(workspaceId);

        results?.forEach(({ channel, accountIds }) => {
          dispatch(
            PRIVATE_ACTIONS.initSelectedAccountIds(accountIds, channel.replace('reviews_', ''))
          );
        });
        setState({ connectedAccountsFetched: true });
      } catch (error) {
        notifications.displayError(error);
      }
    },

  createReviewsAccount:
    (url, channel, onAfterExtraction) =>
    async ({ dispatch }, { currentWorkspaceId }) => {
      dispatch(PRIVATE_ACTIONS.setExtractingCopies(channel, true));

      const result = await createReviewsAccount(
        {
          workspaceId: currentWorkspaceId,
          urls: [url],
          siteType: channel,
        },
        {
          abortSignal: channelAbortControllers.get(channel)?.signal,
        }
      );
      dispatch(
        ACTIONS.extractTextVariations({
          channel,
          selectedAccountIds: [result.id],
          onAfterExtraction,
        })
      );
    },

  createWebsiteAccount:
    (urls, channel, onAfterExtraction) =>
    async ({ dispatch }, { currentWorkspaceId }) => {
      dispatch(PRIVATE_ACTIONS.setExtractingCopies(channel, true));

      const result = await createWebsiteAccount(
        {
          workspaceId: currentWorkspaceId,
          websiteType: channel,
          urls,
        },
        {
          abortSignal: channelAbortControllers.get(channel)?.signal,
        }
      );
      dispatch(
        ACTIONS.extractTextVariations({
          channel,
          selectedAccountIds: [result.id],
          onAfterExtraction,
        })
      );
    },
  setLoadingConnectedAccounts:
    (loadingConnectedAccounts) =>
    ({ setState }) => {
      setState({ loadingConnectedAccounts });
    },

  extractTextVariations:
    ({ channel, selectedAccountIds, onAfterExtraction }) =>
    async ({ dispatch }, { notifications, currentWorkspaceId }) => {
      dispatch(PRIVATE_ACTIONS.setExtractingCopies(channel, true));

      const alignedChannel = alignChannelWithBackend(channel);

      channelAbortControllers.set(channel, new AbortController());

      try {
        await dispatch(
          ACTIONS.updateConnectedAccounts({
            selectedAccountIds,
            channel,
          })
        );

        await extractPerformanceCenterTextVariations(
          {
            accountIds: selectedAccountIds,
            workspaceId: currentWorkspaceId,
            assetType: null,
            dataSource: alignedChannel,
            insightsIntervalDays: null,
          },
          {
            abortSignal: channelAbortControllers.get(channel)?.signal,
          }
        );

        const setAvgCompletionPercent = (avgCompletionPercent, channel) => {
          dispatch(PRIVATE_ACTIONS.setAvgCompletionPercent(avgCompletionPercent, channel));
        };

        setAvgCompletionPercent(0, channel);

        const result = await waitForCopiesExtraction({
          channel: alignedChannel,
          workspaceId: currentWorkspaceId,
          notifications,
          options: {
            abortSignal: channelAbortControllers.get(channel)?.signal,
          },
          setAvgCompletionPercent,
          textVariationsResultsCount: CIP_ADS_PER_PAGE,
        });

        if (onAfterExtraction) {
          await onAfterExtraction();
        }

        channelAbortControllers.delete(channel);

        dispatch(PRIVATE_ACTIONS.initCopyList(result?.copies, channel));
        dispatch(PRIVATE_ACTIONS.initSelectedAccountIds(result?.accountIds, channel));
        dispatch(PRIVATE_ACTIONS.initLastExtractionDate(result?.updatedAt, channel));
        dispatch(
          PRIVATE_ACTIONS.initTags(result?.tagsForSuggestion, result?.tagsForFilter, channel)
        );
        dispatch(PRIVATE_ACTIONS.initCampaigns(result?.campaigns, channel));

        const additionalData = await queryPerformanceCenterAdditionalData(
          alignedChannel,
          currentWorkspaceId
        );

        dispatch(
          PRIVATE_ACTIONS.initTalkingPointList(
            additionalData?.performanceCenterTalkingPoints,
            channel
          )
        );
        dispatch(
          PRIVATE_ACTIONS.setChannelCampaignsCopyImpressions(
            additionalData?.campaignsCopyImpressions,
            channel
          )
        );

        await dispatch(PRIVATE_ACTIONS.queryRestOfTextVariationsData(channel, currentWorkspaceId));
      } catch (error) {
        dispatch(PRIVATE_ACTIONS.initLastExtractionDate(null, channel));
        if (isAsyncRequestError(error)) {
          dispatch(ACTIONS.queryChannelsAuthState());
          dispatch(PRIVATE_ACTIONS.clearSelectedAccountIds(channel));
        }
        if (isCopiesExtractionError(error)) {
          dispatch(ACTIONS.queryChannelsAuthState(true));
          dispatch(ACTIONS.setAuthErrorMessage(channel, error.message));
        }
        if (error.name !== 'AbortError') {
          notifications.displayError(error);
        }
      } finally {
        dispatch(PRIVATE_ACTIONS.setExtractingCopies(channel, false));
        dispatch(PRIVATE_ACTIONS.setLoadingCopies(channel, false));
        dispatch(PRIVATE_ACTIONS.setAvgCompletionPercent(null, channel));
      }
    },

  updateConnectedAccounts:
    ({ channel, selectedAccountIds }) =>
    async ({ dispatch }, { notifications, currentWorkspaceId }) => {
      try {
        await performanceCenterUpdateConnectedAccounts({
          workspaceId: currentWorkspaceId,
          accountIds: selectedAccountIds,
          channel: alignChannelWithBackend(channel),
        });
        dispatch(PRIVATE_ACTIONS.setChannelSelectedAccountIds(channel, selectedAccountIds));
      } catch (error) {
        notifications.displayError(error);
      }
    },

  getChannelLastExtraction:
    (channel) =>
    async ({ dispatch }, { notifications, currentWorkspaceId }) => {
      const alignedChannel = alignChannelWithBackend(channel);

      const lastExtractionDate = dispatch(PRIVATE_ACTIONS.getChannelLastExtractionDate(channel));

      const loadingCopies = dispatch(PRIVATE_ACTIONS.getChannelLoadingCopies(channel));
      const extractingCopies = dispatch(PRIVATE_ACTIONS.getChannelExtractingCopies(channel));

      dispatch(ACTIONS.setLoadingConnectedAccounts(false));

      // don't fetch copies again if already did on same session
      if ((lastExtractionDate && !extractingCopies) || loadingCopies) {
        return;
      }

      dispatch(PRIVATE_ACTIONS.setLoadingCopies(channel, true));

      channelAbortControllers.set(channel, new AbortController());

      const setAvgCompletionPercent = (avgCompletionPercent, channel) => {
        dispatch(PRIVATE_ACTIONS.setAvgCompletionPercent(avgCompletionPercent, channel));
      };

      try {
        const result = await waitForCopiesExtraction({
          channel: alignedChannel,
          workspaceId: currentWorkspaceId,
          notifications,
          options: {
            abortSignal: channelAbortControllers.get(channel)?.signal,
          },
          setAvgCompletionPercent,
          textVariationsResultsCount: CIP_ADS_PER_PAGE,
        });
        channelAbortControllers.delete(channel);

        dispatch(PRIVATE_ACTIONS.initCopyList(result?.copies, channel));
        dispatch(PRIVATE_ACTIONS.initSelectedAccountIds(result?.accountIds, channel));
        dispatch(PRIVATE_ACTIONS.initLastExtractionDate(result?.updatedAt, channel));
        dispatch(
          PRIVATE_ACTIONS.initTags(result?.tagsForSuggestion, result?.tagsForFilter, channel)
        );
        dispatch(PRIVATE_ACTIONS.initCampaigns(result?.campaigns, channel));

        const additionalData = await queryPerformanceCenterAdditionalData(
          alignedChannel,
          currentWorkspaceId
        );

        dispatch(
          PRIVATE_ACTIONS.initTalkingPointList(
            additionalData?.performanceCenterTalkingPoints,
            channel
          )
        );
        dispatch(
          PRIVATE_ACTIONS.setChannelCampaignsCopyImpressions(
            additionalData?.campaignsCopyImpressions,
            channel
          )
        );

        await dispatch(PRIVATE_ACTIONS.queryRestOfTextVariationsData(channel, currentWorkspaceId));
      } catch (error) {
        dispatch(PRIVATE_ACTIONS.initLastExtractionDate(null, channel));
        if (isAsyncRequestError(error)) {
          dispatch(ACTIONS.queryChannelsAuthState());
          dispatch(PRIVATE_ACTIONS.clearSelectedAccountIds(channel));
        }
        if (isCopiesExtractionError(error)) {
          dispatch(ACTIONS.queryChannelsAuthState(true));
          dispatch(ACTIONS.setAuthErrorMessage(channel, error.message));
        }
        if (error.name !== 'AbortError') {
          throw error;
        }
      } finally {
        dispatch(PRIVATE_ACTIONS.setLoadingCopies(channel, false));
        dispatch(PRIVATE_ACTIONS.setAvgCompletionPercent(null, channel));
      }
    },

  sortCopies:
    (field, channel) =>
    ({ getState, setState, dispatch }) => {
      const copies = dispatch(PRIVATE_ACTIONS.getChannelCopies(channel));
      const { copiesSortField, copiesSortDirection } = getState();

      const sameField = copiesSortField === field;
      const sortedAsc = copiesSortDirection === SORT_DIRECTION.asc;
      const newDirection = sameField && sortedAsc ? SORT_DIRECTION.desc : SORT_DIRECTION.asc;

      const sorted = orderBy(copies, [field], [newDirection]);

      dispatch(PRIVATE_ACTIONS.setChannelCopies(channel, sorted));

      setState({
        copiesSortField: field,
        copiesSortDirection: newDirection,
      });
    },

  sortAccounts:
    (field, channel) =>
    ({ getState, setState, dispatch }) => {
      const accounts = dispatch(PRIVATE_ACTIONS.getChannelAccounts(channel));
      const { accountsSortField, accountsSortDirection } = getState();

      const sameField = accountsSortField === field;
      const sortedAsc = accountsSortDirection === SORT_DIRECTION.asc;
      const newDirection = sameField && sortedAsc ? SORT_DIRECTION.desc : SORT_DIRECTION.asc;

      const sorted = orderBy(accounts, [field], [newDirection]);

      dispatch(PRIVATE_ACTIONS.setChannelAccounts(channel, sorted));

      setState({
        accountsSortField: field,
        accountsSortDirection: newDirection,
      });
    },

  getPerformanceCenterResultDocument:
    (adId) =>
    async (_, { notifications, currentCustomerId }) => {
      try {
        const projectId = await getPerformanceCenterResultDocument(adId, currentCustomerId);

        return projectId;
      } catch (error) {
        notifications.displayError(error);
      }
    },

  getChannelSelectedAccountIds: PRIVATE_ACTIONS.getChannelSelectedAccountIds,
  setExtractingCopies: PRIVATE_ACTIONS.setExtractingCopies,

  updateTag:
    ({ channel, copyText, tagName, actionType }) =>
    async ({ setState, dispatch }, { currentWorkspaceId, notifications }) => {
      setState({ updatingTag: true });
      const otherChannels = dispatch(PRIVATE_ACTIONS.getOtherChannels(channel));
      const copies = dispatch(PRIVATE_ACTIONS.getChannelCopies(channel));
      const otherChannelsAds = [];
      otherChannels.forEach((otherChannel) => {
        otherChannelsAds.push(...dispatch(PRIVATE_ACTIONS.getChannelCopies(otherChannel)));
      });
      const selectedAd = copies.find((ad) => ad.text === copyText);
      // get the ad from other channels with the same copy as the selected one
      const otherChannelSelectedAd = otherChannelsAds.find((ad) => ad.text === copyText);

      try {
        const result = await performanceCenterUpdateTag({
          copyText,
          workspaceId: currentWorkspaceId,
          tagName,
          actionType,
        });

        switch (actionType) {
          case CIP_TAG_ACTION_TYPE.rejectForever:
            [...otherChannelsAds, ...copies].forEach((ad) => {
              ad.tags = ad.tags.filter((tag) => tag.tagName !== tagName);
            });
            dispatch(PRIVATE_ACTIONS.removeTagForSuggestion(tagName));
            dispatch(PRIVATE_ACTIONS.removeTagForFilter(channel, tagName));
            otherChannels.forEach((otherChannel) => {
              dispatch(PRIVATE_ACTIONS.removeTagForFilter(otherChannel, tagName));
            });
            break;
          case CIP_TAG_ACTION_TYPE.rejectOne:
            selectedAd.tags = selectedAd.tags.filter((tag) => tag.tagName !== tagName);
            // if we have same copy also on the other channel we want to remove the tag from it as well
            if (otherChannelSelectedAd) {
              otherChannelSelectedAd.tags = selectedAd.tags.filter(
                (tag) => tag.tagName !== tagName
              );
            }
            break;
          case CIP_TAG_ACTION_TYPE.custom:
            selectedAd.tags = [
              ...selectedAd.tags,
              { tagName: result.tagName, status: result.status },
            ];
            dispatch(
              PRIVATE_ACTIONS.addTagForFilter(channel, {
                tagName,
                status: CIP_TAG_STATUS.userAdded,
              })
            );
            // if we have same copy also on the other channel we want to add the tag to it as well
            if (otherChannelSelectedAd) {
              otherChannelSelectedAd.tags = [
                ...selectedAd.tags,
                { tagName: result.tagName, status: result.status },
              ];
              // also add the tag for filter in the other channel

              otherChannels.forEach((otherChannel) => {
                dispatch(
                  PRIVATE_ACTIONS.addTagForFilter(otherChannel, {
                    tagName,
                    status: CIP_TAG_STATUS.userAdded,
                  })
                );
              });
            }

            // do not add tag to tagsForSuggestion if it's existing
            if (result.status === CIP_TAG_STATUS.accepted) {
              return;
            }

            dispatch(
              PRIVATE_ACTIONS.addTagForSuggestion({ tagName, status: CIP_TAG_STATUS.userAdded })
            );
            break;
          case CIP_TAG_ACTION_TYPE.accept:
            selectedAd.tags = selectedAd.tags.map((tag) =>
              tag.tagName === result.tagName ? { ...tag, status: result.status } : tag
            );
            if (otherChannelSelectedAd) {
              otherChannelSelectedAd.tags = otherChannelSelectedAd.tags.map((tag) =>
                tag.tagName === result.tagName ? { ...tag, status: result.status } : tag
              );
              otherChannels.forEach((otherChannel) => {
                dispatch(
                  PRIVATE_ACTIONS.addTagForFilter(otherChannel, {
                    tagName,
                    status: CIP_TAG_STATUS.userAdded,
                  })
                );
              });
            }
            break;
          default:
            new Error('Unknown action type');
        }

        dispatch(PRIVATE_ACTIONS.mergeChannelCopies(channel, copies));
      } catch (error) {
        notifications.displayError(error);
      } finally {
        setState({ updatingTag: false });
      }
    },

  getProjectForNewCopies:
    (channel) =>
    async (_, { currentWorkspaceId }) => {
      const result = await queryProjectForNewCopies(channel, currentWorkspaceId);

      return result?.projectForNewCopies;
    },

  clearStore: PRIVATE_ACTIONS.clearStore,

  cancelWaitRequests: (channel) => () => {
    if (channel) {
      if (channelAbortControllers.has(channel)) {
        channelAbortControllers.get(channel)?.abort();
        channelAbortControllers.delete(channel);
      }
    } else {
      channelAbortControllers.forEach((channelAbortController) => {
        channelAbortController.abort();
      });
      channelAbortControllers.clear();
    }
  },

  restoreWorkspaceDataFromCache:
    (workspaceId) =>
    ({ getState, setState }) => {
      const { workspacesDataCache } = getState();

      if (workspacesDataCache[workspaceId]) {
        setState(workspacesDataCache[workspaceId]);
      }
    },

  setAuthErrorMessage:
    (channel, errorMessage) =>
    ({ getState, setState }) => {
      const { authErrorMessage } = getState();

      setState({
        authErrorMessage: {
          ...authErrorMessage,
          [channel]: errorMessage,
        },
      });
    },
  clearAuthErrorMessage:
    (channel) =>
    ({ getState, setState }) => {
      const { authErrorMessage } = getState();

      delete authErrorMessage[channel];

      setState({
        authErrorMessage,
      });
    },

  getChannelAuthState:
    (channel) =>
    ({ getState }) => {
      return getState().authState[channel];
    },
};

export const PerformanceStore = createStore({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: NAME,
});
